/**
 * <sd-shipping-protect> — the "Premium Shipping Insurance" switch above the cart total.
 *
 * Adds/removes ONE real cart line (the insurance product) over AJAX. Like
 * <sd-cart-upsell-card>, it does not repaint the cart itself: Horizon's
 * <cart-items-component> listens on `document` for a CartLinesUpdateEvent and
 * morphs the section from the `sections` payload the cart response carries. So
 * the contract product-form.js uses is followed exactly:
 *
 *   1. create a deferred promise and dispatch CartLinesUpdateEvent immediately,
 *   2. POST to cart_add_url (on) or cart_change_url (off) asking for the cart
 *      section ids,
 *   3. resolve that promise with the fresh cart + the returned sections.
 *
 * Two things make this different from the upsell card:
 *
 * - It is STATEFUL. The checkbox must mirror the cart, not the last click. The
 *   cart can change from anywhere (the shopper removes the line from the items
 *   list on the cart page, a discount app clears the cart, a second tab). So
 *   `checked` is re-derived from Liquid on every render, and #sync() re-reads
 *   /cart.js after any cart mutation this element did not initiate.
 *
 * - Turning it OFF needs a LINE KEY, not a variant id: /cart/change.js takes
 *   `id` as a line key, and CartLinesUpdateEvent likewise wants `{ id }` on a
 *   remove where it wants `{ merchandiseId }` on an add. Liquid writes the
 *   current key into data-line-key on every render; when that is missing (the
 *   element was added optimistically and not re-rendered since)
 *   #resolveLineKey() falls back to reading /cart.js.
 *
 * Markup contract:
 *   <sd-shipping-protect
 *     data-variant-id="123" data-line-key="123:abc" data-in-cart="true|false">
 *     <input type="checkbox" ref="toggle" on:change="/toggle">
 *     <span ref="status" role="status"></span>
 *   </sd-shipping-protect>
 */

import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const ERROR_DISPLAY_MS = 5000;

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} toggle - The switch itself.
 * @property {HTMLElement} [status] - Live region for add/remove confirmations.
 *
 * @extends {Component<Refs>}
 */
class SdShippingProtect extends Component {
  requiredRefs = ['toggle'];

  #busy = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** Tooltip listeners, torn off in one call on disconnect. */
  #abortController = new AbortController();

  /**
   * Tooltip listeners specifically, torn off and rebuilt on every re-render.
   *
   * #bindTooltip runs again from updatedCallback because morph may have swapped
   * the (i) and its bubble for fresh nodes. Listeners bound to those nodes die
   * with them, but the reposition listeners live on `window` and would otherwise
   * accumulate one extra copy per cart update.
   *
   * @type {AbortController | undefined}
   */
  #tooltipController;

  /**
   * True while the tooltip was opened by hover/focus rather than a click, which
   * is what decides whether leaving the button should close it again.
   */
  #openedByHover = false;

  /**
   * Our own dispatch bubbles to `document` too, so it would re-enter #sync and
   * race the request still in flight. Identified by target containment, the
   * same test component-cart-items.js uses (#isExternalCartUpdate) — the detail
   * payload comes from the hosted @shopify/events module, so its shape is not
   * ours to depend on, whereas the target is plain DOM.
   *
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    const { target } = event;
    if (target instanceof Node && (this === target || this.contains(target))) return;
    this.#sync();
  };

  connectedCallback() {
    super.connectedCallback();
    // Any other cart mutation (a line removed from the items list, the upsell
    // card adding something) re-renders this element in most cases — but not
    // all, since morph may leave an unchanged node untouched. Re-reading the
    // cart is the cheap way to guarantee the switch never lies.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#bindTooltip();
  }

  /**
   * Hover/focus support for the (i) tooltip.
   *
   * The CLICK path needs no JS at all — the button's `popovertarget` is native.
   * This adds the pointer path, and deliberately calls showPopover() rather
   * than letting CSS reveal the bubble: a popover shown by CSS alone never
   * enters the top layer, so inside the cart drawer's stacking context it would
   * paint behind the panel and `position-anchor` would not apply. Routing both
   * paths through the popover API means one visible state to reason about.
   *
   * Everything is guarded: the tooltip only renders when a description exists,
   * and `popover` is unsupported in older Safari — where it is, the button
   * simply does nothing on hover and the text stays available to screen readers
   * through the aria-describedby element.
   */
  #bindTooltip() {
    // Drop the previous render's listeners before adding this render's, so the
    // window-level reposition handlers below never stack up.
    this.#tooltipController?.abort();

    const button = this.querySelector('.sd-sp__info');
    const tip = this.querySelector('.sd-sp__tip');
    if (!(button instanceof HTMLElement) || !(tip instanceof HTMLElement)) return;
    if (typeof tip.showPopover !== 'function') return;

    this.#tooltipController = new AbortController();
    const signal = this.#tooltipController.signal;

    // Browsers WITHOUT CSS anchor positioning. There the stylesheet cannot place
    // the bubble over the (i) at all: an open popover sits in the top layer, where
    // `position: absolute` resolves against the viewport rather than any ancestor,
    // so neither the button nor the title row can act as its containing block. CSS
    // alone therefore lands it over the heading — which is exactly the bug this
    // fixes. Where anchor positioning IS supported the stylesheet already handles
    // it and this measuring path stays switched off.
    const needsManualPosition = !CSS.supports('anchor-name: --a');

    const place = () => {
      if (!needsManualPosition) return;
      const b = button.getBoundingClientRect();
      const t = tip.getBoundingClientRect();
      const gap = 10;

      // Centre on the button, then keep the whole bubble on screen.
      const margin = 8;
      let left = b.left + b.width / 2 - t.width / 2;
      left = Math.min(Math.max(left, margin), window.innerWidth - t.width - margin);

      tip.style.left = `${left}px`;
      tip.style.top = `${b.top - t.height - gap}px`;

      // Point the tail at the button's real centre, which is no longer the
      // bubble's centre once the clamp above has shifted it.
      tip.style.setProperty('--sd-sp-tail-x', `${b.left + b.width / 2 - left}px`);
    };

    const show = () => {
      // Already open (the shopper clicked it) — calling showPopover twice
      // throws an InvalidStateError.
      if (tip.matches(':popover-open')) return;
      try {
        tip.showPopover();
        // After showPopover so the bubble has been laid out and measures true.
        place();
      } catch {
        // Raced with a click that opened it; nothing to do.
      }
    };

    // The native click path opens the popover without going through show(), and
    // scrolling the drawer moves the button out from under an already-open bubble.
    tip.addEventListener('toggle', (event) => {
      if (/** @type {ToggleEvent} */ (event).newState === 'open') place();
    }, { signal });
    if (needsManualPosition) {
      const reposition = () => { if (tip.matches(':popover-open')) place(); };
      window.addEventListener('resize', reposition, { signal });
      // Capture: the drawer's own scroller is what actually moves, not the window.
      window.addEventListener('scroll', reposition, { signal, capture: true, passive: true });
    }

    const hide = () => {
      if (!tip.matches(':popover-open')) return;
      // Only close what hover opened. If the pointer leaves while the bubble is
      // still wanted (it was clicked open on touch), the native light-dismiss
      // handles it instead.
      if (this.#openedByHover) tip.hidePopover();
    };

    button.addEventListener('pointerenter', () => { this.#openedByHover = true; show(); }, { signal });
    button.addEventListener('pointerleave', hide, { signal });
    // Keyboard parity: the tooltip follows focus the same way it follows hover.
    button.addEventListener('focus', () => { this.#openedByHover = true; show(); }, { signal });
    button.addEventListener('blur', hide, { signal });
    // A real click takes ownership, so pointerleave stops closing it.
    button.addEventListener('click', () => { this.#openedByHover = false; }, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#abortController.abort();
    this.#tooltipController?.abort();
    clearTimeout(this.#timer);
  }

  /**
   * The Section Rendering API re-rendered us — Liquid has just written a fresh
   * data-in-cart, so trust the markup and drop any optimistic state.
   */
  updatedCallback() {
    // super first: the base class re-reads this.refs here, and skipping it
    // would leave `toggle` pointing at the pre-morph input.
    super.updatedCallback();
    // morph may have replaced the (i) button and its bubble, in which case the
    // listeners bound on connect are attached to detached nodes. Re-binding is
    // idempotent: #bindTooltip only ever adds listeners to the nodes currently
    // in the DOM, and the old ones die with the nodes they were on.
    this.#bindTooltip();
    if (this.#busy) return;
    if (this.refs.toggle) this.refs.toggle.checked = this.dataset.inCart === 'true';
  }

  /** Cart section ids to ask for, so every visible cart surface repaints. */
  get #sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
      .filter((id) => Boolean(id));
  }

  async toggle() {
    const { toggle } = this.refs;

    // Re-entry guard. The checkbox has already flipped visually (that is the
    // browser's doing, and it is the optimistic state we want), so on a
    // double-fire we simply put it back rather than firing a second request.
    if (this.#busy) {
      toggle.checked = !toggle.checked;
      return;
    }

    const wantOn = toggle.checked;
    this.#busy = true;
    toggle.disabled = true;
    this.dataset.loading = '';

    // Resolved before dispatch because the two actions describe a line
    // differently: `add` names the merchandise (a variant id), `remove` names
    // the line already in the cart (a line key). Same split as
    // component-cart-items.js, which sends `{ id: lineId }` on remove.
    const lineKey = wantOn ? '' : await this.#resolveLineKey();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      wantOn
        ? new CartLinesUpdateEvent({
            action: 'add',
            context: 'cart',
            lines: [{ merchandiseId: this.dataset.variantId ?? '', quantity: 1 }],
            promise: deferred.promise,
          })
        : new CartLinesUpdateEvent({
            action: 'remove',
            context: 'cart',
            lines: [{ id: lineKey, quantity: 0 }],
            promise: deferred.promise,
          })
    );

    try {
      const result = wantOn ? await this.#add() : await this.#remove(lineKey);

      const cart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      // Resolving with `sections` is what actually repaints the cart.
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'sd-shipping-protect',
          sections: result.sections,
          didError: false,
        },
      });

      this.dataset.inCart = String(wantOn);
      this.#announce(wantOn ? this.dataset.labelOn : this.dataset.labelOff);
      this.#release();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Cart update failed';
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      // Put the switch back where the cart actually is — leaving it flipped
      // after a failed add would misrepresent what the shopper is buying.
      toggle.checked = !wantOn;
      this.#announce(message || this.dataset.labelError, true);
      this.#release(ERROR_DISPLAY_MS);
    }
  }

  async #add() {
    const body = new FormData();
    body.set('id', this.dataset.variantId ?? '');
    body.set('quantity', '1');
    const ids = this.#sectionIds;
    if (ids.length) {
      body.set('sections', ids.join(','));
      body.set('sections_url', window.location.pathname);
    }

    const result = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body,
    }).then((r) => r.json());

    // A 422 (e.g. inventory ran out) returns a body with `status` set rather
    // than throwing, so it has to be checked explicitly.
    if (result.status) throw new Error(result.description || result.message || 'Add to cart failed');
    return result;
  }

  /** @param {string} key - The line key, already resolved by toggle(). */
  async #remove(key) {
    // Already gone (removed from the item list, or a second tab beat us to it).
    // Posting quantity:0 for a missing key would 404, so this is treated as
    // success — the switch settles off instead of bouncing back on. `sections`
    // is undefined here, which resolve() below turns into a section re-render
    // rather than a stale total.
    if (!key) return {};

    const ids = this.#sectionIds;
    // `id` (the line key), not `line` (a 1-based index) — the index shifts
    // whenever another line is removed, and this element can be several cart
    // mutations behind its own last render.
    // sections_url matches component-cart-items.js: without it Shopify renders
    // the requested sections against the wrong template on non-cart pages.
    const body = JSON.stringify({
      id: key,
      quantity: 0,
      sections_url: window.location.pathname,
      ...(ids.length ? { sections: ids.join(',') } : {}),
    });

    const result = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body })).then((r) =>
      r.json()
    );

    if (result.status || result.errors) {
      throw new Error(result.description || result.errors || result.message || 'Cart update failed');
    }
    return result;
  }

  /**
   * This element's line in the live cart, or undefined when it is not in the
   * bag. Reads /cart.js — the authority both #sync and #resolveLineKey defer to.
   *
   * @returns {Promise<{ key: string, variant_id: number } | undefined>}
   */
  async #findLine() {
    const cart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());

    const id = Number(this.dataset.variantId);
    return /** @type {Array<{ key: string, variant_id: number }>} */ (cart.items ?? []).find(
      (item) => item.variant_id === id
    );
  }

  /**
   * The line key for this variant. data-line-key is written by Liquid on every
   * render and is right almost always; the /cart.js read covers the case where
   * this element was added optimistically and has not been re-rendered since.
   */
  async #resolveLineKey() {
    if (this.dataset.lineKey) return this.dataset.lineKey;
    return (await this.#findLine())?.key || '';
  }

  /**
   * Re-read the cart and match the switch to it. Runs after cart changes this
   * element did not make, so removing the insurance line from the cart page's
   * item list flips the switch off rather than leaving it stuck on.
   */
  async #sync() {
    if (this.#busy) return;
    try {
      const line = await this.#findLine();
      if (!this.isConnected) return;

      this.dataset.inCart = String(Boolean(line));
      this.dataset.lineKey = line?.key || '';
      if (this.refs.toggle) this.refs.toggle.checked = Boolean(line);
    } catch {
      // A failed sync leaves the last known state in place; the next render
      // from Liquid corrects it. Nothing useful to show the shopper here.
    }
  }

  /**
   * @param {string} [message]
   * @param {boolean} [isError]
   */
  #announce(message, isError = false) {
    const { status } = this.refs;
    if (!status) return;
    status.textContent = message || '';
    this.dataset.state = isError ? 'error' : '';
  }

  /**
   * Hand the switch back to the shopper. Guarded: morph may have replaced us.
   * @param {number} [delay]
   */
  #release(delay = 1200) {
    delete this.dataset.loading;
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.#busy = false;
      if (!this.isConnected) return;
      if (this.refs.toggle) this.refs.toggle.disabled = false;
      this.#announce('');
    }, delay);
  }
}

if (!customElements.get('sd-shipping-protect')) {
  customElements.define('sd-shipping-protect', SdShippingProtect);
}
